import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Typography,
} from "@mui/material";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import StatusChip from "../../components/StatusChip";
import TableActions from "../../components/TableActions";
import ActionButton from "../../components/ActionButton";
import ThemedDataGrid from "../../components/ThemedDataGrid";

import { graphqlEndpoint } from "../../config";
import formOptions from "../../config/formOptions.json";

import { READ_AUDIT_QUESTIONS } from "../../graph/auditQuestions/queries";
import { DELETE_AUDIT_QUESTION } from "../../graph/auditQuestions/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const formatEnumLabel = (value) => {
  if (!value) {
    return "Not assigned";
  }

  return value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const ManageAuditQuestions = () => {
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [industryFilter, setIndustryFilter] = useState("");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const fetchQuestions = useCallback(async () => {
    try {
      const variables = industryFilter
        ? { industry: industryFilter }
        : {};

      const data = await client.request(
        READ_AUDIT_QUESTIONS,
        variables,
      );

      const auditQuestions = Array.isArray(
        data?.readAuditQuestions,
      )
        ? data.readAuditQuestions
        : [];

      setQuestions(
        auditQuestions.map((question) => ({
          id: question.id,
          industry: question.industry,
          area: question.area || "",
          statement: question.statement || "",
          description: question.description || "",
          sortOrder: question.sortOrder ?? 0,
          isActive: Boolean(question.isActive),
          isHighImpact: Boolean(question.isHighImpact),
        })),
      );
    } catch (error) {
      console.error(
        "Failed to fetch audit questions:",
        error,
      );

      setQuestions([]);
    }
  }, [industryFilter]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const handleEdit = (id) => {
    navigate(`/auditQuestions/edit/${id}`);
  };

  const handleOpenDeleteModal = (question) => {
    setSelectedQuestion(question);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setSelectedQuestion(null);
    setDeleteModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!selectedQuestion?.id) {
      return;
    }

    try {
      await client.request(DELETE_AUDIT_QUESTION, {
        id: selectedQuestion.id,
      });

      await fetchQuestions();
      handleCloseDeleteModal();
    } catch (error) {
      console.error(
        "Failed to deactivate audit question:",
        error,
      );
    }
  };

  const columns = [
    {
      field: "statement",
      headerName: "Process / Activity",
      flex: 1.5,
      minWidth: 220,
      cellClassName: "name-column--cell",
    },
    {
      field: "industry",
      headerName: "Industry",
      flex: 0.8,
      minWidth: 130,
      renderCell: ({ value }) => (
        <StatusChip
          label={formatEnumLabel(value)}
          tone="default"
        />
      ),
    },
    {
      field: "area",
      headerName: "Area",
      flex: 1,
      minWidth: 160,
      valueGetter: (value, row) =>
        formatEnumLabel(row?.area ?? value),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.8,
      minWidth: 260,
    },
    {
      field: "sortOrder",
      headerName: "Order",
      flex: 0.45,
      minWidth: 80,
      type: "number",
    },
    {
      field: "isHighImpact",
      headerName: "High Impact",
      flex: 0.7,
      minWidth: 110,
      renderCell: ({ value }) => (
        <StatusChip
          label={value ? "High" : "Standard"}
          tone={value ? "warning" : "default"}
        />
      ),
    },
    {
      field: "isActive",
      headerName: "Active",
      flex: 0.6,
      minWidth: 100,
      renderCell: ({ value }) => (
        <StatusChip
          label={value ? "Active" : "Inactive"}
          tone={value ? "success" : "default"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.7,
      minWidth: 110,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <TableActions
          onEdit={() => handleEdit(row.id)}
          onDelete={() => handleOpenDeleteModal(row)}
          hideCreate
        />
      ),
    },
  ];

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <Header
          title="MANAGE AUDIT QUESTIONS"
          subtitle="Create and manage the global operational audit question bank"
        />

        <ActionButton
          tone="gold"
          onClick={() =>
            navigate("/createAuditQuestions")
          }
        >
          Create Question
        </ActionButton>
      </Box>

      <Box
        display="flex"
        gap={2}
        alignItems="center"
        mb={2}
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="industry-filter-label">
            Industry
          </InputLabel>

          <Select
            labelId="industry-filter-label"
            value={industryFilter}
            label="Industry"
            onChange={(event) =>
              setIndustryFilter(event.target.value)
            }
          >
            <MenuItem value="">All Industries</MenuItem>

            {formOptions.industryOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <ThemedDataGrid
        rows={questions}
        columns={columns}
        initialState={{
          sorting: {
            sortModel: [
              {
                field: "sortOrder",
                sort: "asc",
              },
            ],
          },
        }}
      />

      <Modal
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
      >
        <Box
          p={4}
          bgcolor="background.paper"
          borderRadius="12px"
          mx="auto"
          my="20vh"
          width="420px"
          maxWidth="calc(100% - 32px)"
          boxShadow={24}
        >
          <Typography variant="h6" mb={2}>
            Deactivate Audit Question
          </Typography>

          <Typography mb={3}>
            Are you sure you want to deactivate{" "}
            <strong>
              {selectedQuestion?.statement ||
                "this audit question"}
            </strong>
            ? It will no longer appear in new audits, but
            historical audit responses will remain intact.
          </Typography>

          <Box
            display="flex"
            justifyContent="flex-end"
            gap={2}
          >
            <Button onClick={handleCloseDeleteModal}>
              Cancel
            </Button>

            <Button
              color="error"
              variant="contained"
              onClick={handleConfirmDelete}
            >
              Deactivate
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ManageAuditQuestions;